/**
 * @typedef {Object} InjectionSearchRow
 * @property {string} id
 * @property {string} name
 * @property {number} [analytesCount]
 * @property {boolean} [unavailable] - remembered by the browser, but gone (deleted / no access / no analytes)
 */

/**
 * @typedef {Object} AnalyteRow
 * @property {string} id
 * @property {string} alias
 * @property {string|null} mf
 * @property {number|null} emw
 * @property {string|null} structureSvgPath
 * @property {string|null} [structureSvg]
 */

export default class InjectionSearchRepository {
    #injectionRepository;
    #structureSvgLoader;
    #userSettings;
    #orgId;
    #excludedInjectionId;

    constructor(injectionRepository, structureSvgLoader, userSettings, orgId, excludedInjectionId = null) {
        this.#injectionRepository = injectionRepository;
        this.#structureSvgLoader = structureSvgLoader;
        this.#userSettings = userSettings;
        this.#orgId = orgId || '';
        this.#excludedInjectionId = excludedInjectionId;
    }

    /**
     * @param {string} nameFilter - when the user is searching, the recents aren't shown, so they aren't requested
     * @return {Promise<{recents: InjectionSearchRow[], injections: InjectionSearchRow[]}>}
     */
    async searchInjectionsByName(nameFilter) {
        const remembered = this.#isSearching(nameFilter) ? [] : this.#getRememberedInjections();
        const rows = (await this.#injectionRepository
            .getInjectionsHavingAnalytes(this.#orgId, nameFilter.trim(), remembered.map(injection => injection.id)))
            .filter(injection => injection.id !== this.#excludedInjectionId);
        const rowsById = new Map(rows.map(injection => [injection.id, injection]));
        return {
            recents: remembered.map(injection => rowsById.get(injection.id)
                || {...injection, unavailable: true}),
            injections: rows.filter(injection => !remembered.some(r => r.id === injection.id))
        };
    }

    /**
     * @return {Promise<AnalyteRow[]>}
     */
    async getAnalytes(injectionId) {
        return await this.#injectionRepository.getInjectionAnalytes(injectionId) || [];
    }

    /**
     * @param {AnalyteRow[]} analytes - mutated in place
     */
    async loadStructures(analytes) {
        await Promise.all(analytes
            .filter(analyte => analyte.structureSvgPath && analyte.structureSvg === undefined)
            .map(async analyte => analyte.structureSvg = await this.#loadSvg(analyte.structureSvgPath)));
        return analytes;
    }

    async #loadSvg(path) {
        try {
            return await this.#structureSvgLoader.getByUrlPath(path);
        } catch (e) {
            return null;
        }
    }

    rememberInjection(injection) {
        this.#userSettings.addRecentAnalyteSource(this.#orgId, {id: injection.id, name: injection.name});
    }

    forgetInjection(injectionId) {
        this.#userSettings.removeRecentAnalyteSource(this.#orgId, injectionId);
    }

    #getRememberedInjections() {
        return this.#userSettings.getRecentAnalyteSources(this.#orgId)
            .filter(injection => injection.id !== this.#excludedInjectionId);
    }

    #isSearching(nameFilter) {
        return nameFilter.trim().length > 0;
    }
}
